import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import { Download, Plus, Search, Filter, Calendar, AlertCircle, MoreVertical, ExternalLink, X, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface Invoice {
  id: string;
  invoiceNumber: string;
  customer: string;
  customerEmail: string;
  property: string;
  issuedDate: string;
  total: string;
  outstanding: string;
  status: 'OVERDUE' | 'PAID' | 'PARTIALLY PAID' | 'PENDING' | 'VOID';
}

interface LineItem {
  id: string;
  description: string;
  qty: number;
  unitPrice: number;
  taxPercent: number;
}

const AdminBillingPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateInvoiceModal, setShowCreateInvoiceModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedProperty, setSelectedProperty] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [currency, setCurrency] = useState('GBP');
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [customerNotes, setCustomerNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [fetchedData, setFetchedData] = useState<any[]>([]);
  const [customers, setCustomers] = useState<{id:string, name:string, email:string}[]>([]);
  const [properties, setProperties] = useState<{id:string, name:string}[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [billingRes, custRes, propRes] = await Promise.all([
          apiService.request('/admin/billing-data', 'GET', { period: 'monthly' }),
          apiService.request('/admin/customers', 'GET'),
          apiService.request('/admin/properties', 'GET')
        ]);

        if (billingRes.success && billingRes.data) {
          setFetchedData(billingRes.data.bookings || []);
          // Compute metrics from billing data
          if (billingRes.data.bookings && Array.isArray(billingRes.data.bookings)) {
            const outstanding = billingRes.data.bookings.filter((b: any) => b.status !== 'PAID').reduce((sum: number, b: any) => sum + ((b.pricePence || 0) / 100), 0);
            const overdue = billingRes.data.bookings.filter((b: any) => b.status !== 'PAID' && new Date(b.dueDate) < new Date()).reduce((sum: number, b: any) => sum + ((b.pricePence || 0) / 100), 0);
            const today = billingRes.data.bookings.filter((b: any) => {
              const date = new Date(b.paidDate);
              const now = new Date();
              return date.toDateString() === now.toDateString();
            }).reduce((sum: number, b: any) => sum + ((b.pricePence || 0) / 100), 0);
            const revenue = billingRes.data.bookings.reduce((sum: number, b: any) => sum + ((b.pricePence || 0) / 100), 0);
            setBillingMetrics({
              outstanding: `$${outstanding.toFixed(2)}`,
              today: `$${today.toFixed(2)}`,
              overdue: `$${overdue.toFixed(2)}`,
              revenue: `$${revenue.toFixed(2)}`
            });
          }
        }

        if (custRes.success && Array.isArray(custRes.data)) {
          setCustomers(custRes.data.map(c => ({
            id: c.id,
            name: c.name || c.business_name || '',
            email: c.email || ''
          })));
        }

        if (propRes.success && Array.isArray(propRes.data)) {
          setProperties(propRes.data.map(p => ({
            id: p.id,
            name: p.address || p.name || ''
          })));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  // Helper functions for line items
  const addLineItem = () => {
    const newItem: LineItem = {
      id: Date.now().toString(),
      description: '',
      qty: 1,
      unitPrice: 0,
      taxPercent: 20,
    };
    setLineItems([...lineItems, newItem]);
  };

  const removeLineItem = (id: string) => {
    setLineItems(lineItems.filter(item => item.id !== id));
  };

  const updateLineItem = (id: string, field: keyof LineItem, value: any) => {
    setLineItems(lineItems.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  // Calculate totals
  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + (item.qty * item.unitPrice), 0);
  };

  const subtotal = calculateSubtotal();
  const calculateVat = () => {
    return lineItems.reduce((sum, item) => {
      const itemTotal = item.qty * item.unitPrice;
      return sum + (itemTotal * item.taxPercent / 100);
    }, 0);
  };

  const vat = calculateVat();
  const processingFee = 0;
  const total = subtotal + vat + processingFee;

  const invoices: Invoice[] = (fetchedData as any[]).map((inv: any) => {
    const amount = inv.pricePence != null ? `$${(inv.pricePence / 100).toFixed(2)}` : (inv.price || '—');
    const status = (inv.status || 'PENDING').toUpperCase() as Invoice['status'];
    return {
      id: String(inv.id),
      invoiceNumber: inv.jobCode || `#INV-${inv.id}`,
      customer: inv.customer || '—',
      customerEmail: inv.email || '',
      property: inv.service || '—',
      issuedDate: inv.bookingDate ? new Date(inv.bookingDate).toLocaleDateString('en-GB') : '—',
      total: amount,
      outstanding: status === 'PAID' ? '£0.00' : amount,
      status,
    };
  });

  const overdueCount = invoices.filter(i => i.status === 'OVERDUE').length;
  const itemsPerPage = 10;
  const totalPages = Math.ceil(invoices.length / itemsPerPage);
  const paginatedInvoices = invoices.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, invoices.length);

  const tabs = [
    { id: 'all', label: 'All', count: invoices.length },
    { id: 'issued', label: 'Issued', count: undefined },
    { id: 'pending', label: 'Pending', count: invoices.filter(i => i.status === 'PENDING').length },
    { id: 'paid', label: 'Paid', count: invoices.filter(i => i.status === 'PAID').length },
    { id: 'partially-paid', label: 'Partially Paid', count: undefined },
    { id: 'overdue', label: 'Overdue', count: overdueCount, badge: overdueCount > 0 },
    { id: 'void', label: 'Void', count: undefined },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'OVERDUE':
        return { dot: 'bg-red-600', text: 'text-red-600' };
      case 'PAID':
        return { dot: 'bg-teal-600', text: 'text-teal-600' };
      case 'PARTIALLY PAID':
        return { dot: 'bg-orange-600', text: 'text-orange-600' };
      case 'PENDING':
        return { dot: 'bg-gray-400', text: 'text-gray-600' };
      case 'VOID':
        return { dot: 'bg-gray-300', text: 'text-gray-500' };
      default:
        return { dot: 'bg-gray-400', text: 'text-gray-600' };
    }
  };

  // These metrics are now computed from API data - see useEffect
  // Fallback values shown if API doesn't provide metrics
  const [billingMetrics, setBillingMetrics] = useState({
    outstanding: '£0.00',
    today: '£0.00',
    overdue: '£0.00',
    revenue: '£0.00'
  });

  const totalOutstanding = billingMetrics.outstanding;
  const collectedToday = billingMetrics.today;
  const overdueAmount = billingMetrics.overdue;
  const monthlyRevenue = billingMetrics.revenue;

  // Handler: Generate invoice
  const handleGenerateInvoice = async (invoiceData: any) => {
    try {
      const response = await apiService.request('/admin/billing/generate-invoice', 'POST', {
        ...invoiceData,
        generatedBy: localStorage.getItem('adminId'),
        generatedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Invoice generated');
        window.location.reload();
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  // Handler: Export billing report
  const handleExportBillingReport = async (period: string) => {
    try {
      const response = await apiService.request('/admin/billing/export', 'GET', {
        period: period
      });
      if (response.success && response.data) {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `billing-${period}-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        alert('✓ Report exported');
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  // Handler: Process refund
  const handleProcessRefund = async (transactionId: string, amount: number) => {
    try {
      const response = await apiService.request(`/admin/billing/refund`, 'POST', {
        transactionId: transactionId,
        amount: amount,
        processedBy: localStorage.getItem('adminId'),
        processedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Refund processed');
        window.location.reload();
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  // Handler: Send invoice reminder
  const handleSendInvoiceReminder = async (invoiceId: string) => {
    try {
      const response = await apiService.request(`/admin/billing/send-reminder`, 'POST', {
        invoiceId: invoiceId,
        sentBy: localStorage.getItem('adminId'),
        sentAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Reminder sent');
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  // Handler: Update billing settings
  const handleUpdateBillingSettings = async (settings: any) => {
    try {
      const response = await apiService.request('/admin/billing/settings', 'PUT', {
        ...settings,
        updatedBy: localStorage.getItem('adminId'),
        updatedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Settings updated');
        window.location.reload();
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  return (
    <>
      <Helmet>
        <title>Billing - VireWire Admin</title>
        <meta name="description" content="Manage customer invoicing and payment collection" />
      </Helmet>

      <AdminLayout>
        <div className="p-8 bg-gray-50 min-h-screen">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-start justify-between mb-8">
              <div>
                <h1 className="text-4xl font-bold text-gray-900">Billing</h1>
                <p className="text-gray-600 mt-2">Customer invoicing and payment collection</p>
              </div>
              <div className="flex items-center gap-3">
                <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 font-bold text-sm rounded-lg hover:bg-gray-50 transition-colors">
                  <Download className="w-4 h-4" />
                  Export
                </button>
                <button
                  onClick={() => setShowCreateInvoiceModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white font-bold text-sm rounded-lg hover:bg-teal-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Create Invoice
                </button>
              </div>
            </div>

            {/* Metrics Cards */}
            <div className="grid grid-cols-4 gap-6 mb-8">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-3">Total Outstanding</p>
                <p className="text-3xl font-bold text-gray-900">{totalOutstanding}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-3">Collected Today</p>
                <p className="text-3xl font-bold text-teal-600">{collectedToday}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-3">Overdue</p>
                <p className="text-3xl font-bold text-red-600">{overdueAmount}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-3">Monthly Revenue</p>
                <p className="text-3xl font-bold text-gray-900">{monthlyRevenue}</p>
              </div>
            </div>

            {/* Alert Box */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <p className="text-sm font-bold text-red-800">
                  {overdueCount} invoices are overdue — total {overdueAmount}. Take action.
                </p>
              </div>
              <button
                onClick={() => setActiveTab('overdue')}
                className="text-red-600 hover:text-red-700 font-bold text-sm uppercase tracking-widest"
              >
                View Overdue
              </button>
            </div>

            {/* Tabs */}
            <div className="mb-8">
              <div className="border-b border-gray-200 flex items-center gap-8">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`pb-4 font-bold text-sm uppercase tracking-widest relative ${
                      activeTab === tab.id
                        ? 'text-gray-900 border-b-2 border-gray-900'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {tab.label}
                      {tab.count !== undefined && (
                        <span className={`${tab.badge ? 'bg-red-600 text-white px-2 py-0.5 rounded text-xs' : 'text-gray-600'}`}>
                          {tab.count}
                        </span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Search and Filters */}
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-3 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search invoices, customers, properties..."
                  className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-200 text-sm"
                />
              </div>
              <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 font-bold text-sm rounded-lg hover:bg-gray-50 transition-colors">
                <Filter className="w-4 h-4" />
                Filter
              </button>
              <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 font-bold text-sm rounded-lg hover:bg-gray-50 transition-colors">
                <Calendar className="w-4 h-4" />
                Date Range
              </button>
            </div>

            {/* Invoice Table */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Invoice #</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Customer</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Property</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Issued Date</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Total</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Outstanding</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Status</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedInvoices.length > 0 ? paginatedInvoices.map((invoice) => {
                      const statusColor = getStatusColor(invoice.status);
                      return (
                        <tr
                          key={invoice.id}
                          className="border-b border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => navigate(`/admin/invoices/${invoice.id}`)}
                        >
                          <td className="px-6 py-4 font-bold text-gray-900">{invoice.invoiceNumber}</td>
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-bold text-gray-900">{invoice.customer}</p>
                              <p className="text-xs text-gray-500">{invoice.customerEmail}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">{invoice.property}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{invoice.issuedDate}</td>
                          <td className="px-6 py-4 font-bold text-gray-900">{invoice.total}</td>
                          <td className={`px-6 py-4 font-bold ${invoice.outstanding === '£0.00' ? 'text-teal-600' : 'text-red-600'}`}>
                            {invoice.outstanding}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${statusColor.dot}`} />
                              <span className={`text-xs font-bold uppercase tracking-widest ${statusColor.text}`}>
                                {invoice.status}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              <ExternalLink className="w-4 h-4 text-gray-600" />
                            </button>
                          </td>
                        </tr>
                      );
                    }) : (
                      <tr>
                        <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                          No invoices found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                <p className="text-xs font-bold text-gray-600 uppercase tracking-widest">
                  Showing {invoices.length === 0 ? 0 : startIndex} to {endIndex} of {invoices.length} results
                </p>
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      className="p-2 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
                      disabled={currentPage === 1}
                    >
                      <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-8 h-8 rounded font-bold text-sm ${
                          currentPage === page
                            ? 'bg-teal-600 text-white'
                            : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      className="p-2 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
                      disabled={currentPage === totalPages}
                    >
                      <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="text-center mt-8 text-xs text-gray-500 uppercase tracking-widest">
              Powered by Virewire Core Service v4.2.1
            </div>
          </div>
        </div>

        {/* Create Invoice Modal */}
        {showCreateInvoiceModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <div>
                  <CardTitle className="text-2xl font-bold">Create New Invoice</CardTitle>
                  <p className="text-sm text-gray-600 mt-1">Draft a new financial request for processing.</p>
                </div>
                <button
                  onClick={() => setShowCreateInvoiceModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Customer & Property Selection */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                      Customer Selection
                    </label>
                    <select
                      value={selectedCustomer}
                      onChange={(e) => setSelectedCustomer(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="">Select Customer...</option>
                      {customers.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                      Property Assignment
                    </label>
                    <select
                      value={selectedProperty}
                      onChange={(e) => setSelectedProperty(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="">Select Property...</option>
                      {properties.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Dates & Currency */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                      Invoice Date
                    </label>
                    <input
                      type="date"
                      // (audit 2026-06-11) — was building YYYY-DD-MM (reverse of
                      // MM/DD/YYYY) producing invalid "2026-18-06"-style values.
                      value={(() => {
                        const [m, d, y] = (invoiceDate || '').split('/');
                        return y && m && d ? `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}` : '';
                      })()}
                      onChange={(e) => {
                        const [year, month, day] = e.target.value.split('-');
                        if (year && month && day) setInvoiceDate(`${month}/${day}/${year}`);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                      Due Date
                    </label>
                    <input
                      type="date"
                      value={(() => {
                        const [m, d, y] = (dueDate || '').split('/');
                        return y && m && d ? `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}` : '';
                      })()}
                      onChange={(e) => {
                        const [year, month, day] = e.target.value.split('-');
                        if (year && month && day) setDueDate(`${month}/${day}/${year}`);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                      Currency
                    </label>
                    <select
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="USD">USD - United States Dollar</option>
                      <option value="GBP">GBP - British Pound</option>
                      <option value="EUR">EUR - Euro</option>
                    </select>
                  </div>
                </div>

                {/* Line Items */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest">
                      Line Items
                    </label>
                    <button
                      onClick={addLineItem}
                      className="text-xs font-bold text-teal-600 hover:text-teal-700 uppercase tracking-widest flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" />
                      ADD ROW
                    </button>
                  </div>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-3 py-2 text-left text-xs font-bold text-gray-600 uppercase">Description</th>
                            <th className="px-3 py-2 text-left text-xs font-bold text-gray-600 uppercase w-16">QTY</th>
                            <th className="px-3 py-2 text-left text-xs font-bold text-gray-600 uppercase w-24">Unit Price</th>
                            <th className="px-3 py-2 text-left text-xs font-bold text-gray-600 uppercase w-16">Tax %</th>
                            <th className="px-3 py-2 text-left text-xs font-bold text-gray-600 uppercase w-20">Amount</th>
                            <th className="px-3 py-2 text-center text-xs font-bold text-gray-600 uppercase w-10">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lineItems.map((item) => (
                            <tr key={item.id} className="border-b border-gray-200 hover:bg-gray-50">
                              <td className="px-3 py-2">
                                <input
                                  type="text"
                                  value={item.description}
                                  onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                  placeholder="Description"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="number"
                                  value={item.qty}
                                  onChange={(e) => updateLineItem(item.id, 'qty', parseInt(e.target.value) || 0)}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                  min="0"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="number"
                                  value={item.unitPrice}
                                  onChange={(e) => updateLineItem(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                  min="0"
                                  step="0.01"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="number"
                                  value={item.taxPercent}
                                  onChange={(e) => updateLineItem(item.id, 'taxPercent', parseFloat(e.target.value) || 0)}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                  min="0"
                                  max="100"
                                />
                              </td>
                              <td className="px-3 py-2 text-right font-bold text-gray-900">
                                {(item.qty * item.unitPrice).toFixed(2)}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <button
                                  onClick={() => removeLineItem(item.id)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Notes Sections */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                      Customer Notes
                    </label>
                    <textarea
                      value={customerNotes}
                      onChange={(e) => setCustomerNotes(e.target.value)}
                      placeholder="Visible to the customer..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      rows={3}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                      Internal Admin Notes
                    </label>
                    <textarea
                      value={internalNotes}
                      onChange={(e) => setInternalNotes(e.target.value)}
                      placeholder="Internal reference only..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      rows={3}
                    />
                  </div>
                </div>

                {/* Totals */}
                <div className="flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-bold text-gray-900">{currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : '$'}{subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">VAT (20%)</span>
                      <span className="font-bold text-gray-900">{currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : '$'}{vat.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Processing Fee</span>
                      <span className="font-bold text-teal-600">{currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : '$'}0.00</span>
                    </div>
                    <div className="flex justify-between text-lg border-t border-gray-200 pt-2">
                      <span className="font-bold text-gray-900">Total</span>
                      <span className="font-bold text-gray-900">{currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : '$'}{total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Modal Actions */}
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => setShowCreateInvoiceModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      // Handle invoice creation
                      setShowCreateInvoiceModal(false);
                      // Reset form
                      setSelectedCustomer('');
                      setSelectedProperty('');
                      setLineItems([{ id: '1', description: '', qty: 1, unitPrice: 0, taxPercent: 20 }]);
                      setCustomerNotes('');
                      setInternalNotes('');
                    }}
                    className="flex-1 px-4 py-2 bg-teal-600 text-white font-bold rounded-lg hover:bg-teal-700 transition-colors"
                  >
                    Create & Send Invoice
                  </button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </AdminLayout>
    </>
  );
};

export default AdminBillingPage;
